import { useState, useRef, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'sonner';
import { useVisibilityPolling } from './useVisibilityPolling';

import { fmtNumber } from '../utils/format';
const ADMIN_FAST = 12000;
const ADMIN_NORMAL = 20000;
const ADMIN_SLOW = 45000;

/**
 * Hook for admin pending deposits (visibility-aware polling)
 */
export const useAdminPendingDeposits = () => {
  const [pendingDeposits, setPendingDeposits] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const previousCount = useRef(0);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/admin/deposits?status=pending');
      const pending = res.data?.deposits || [];
      if (previousCount.current > 0 && pending.length > previousCount.current) {
        const newest = pending[pending.length - 1];
        toast.info('New Deposit Request', { description: `${newest.user_email} - $${fmtNumber(newest.amount, 2)} ${newest.crypto_type}` });
      }
      previousCount.current = pending.length;
      setPendingDeposits(pending);
      setCount(pending.length);
      setLoading(false);
    } catch { setLoading(false); }
  }, []);

  useVisibilityPolling(fetchData, ADMIN_FAST);

  return { pendingDeposits, count, loading };
};

/**
 * Hook for admin pending withdrawals (visibility-aware polling)
 */
export const useAdminPendingWithdrawals = () => {
  const [pendingWithdrawals, setPendingWithdrawals] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const previousCount = useRef(0);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/admin/withdrawals?status=pending');
      const pending = res.data?.withdrawals || [];
      if (previousCount.current > 0 && pending.length > previousCount.current) {
        const newest = pending[pending.length - 1];
        toast.info('New Withdrawal Request', { description: `${newest.user_email} - $${fmtNumber(newest.amount, 2)}` });
      }
      previousCount.current = pending.length;
      setPendingWithdrawals(pending);
      setCount(pending.length);
      setLoading(false);
    } catch { setLoading(false); }
  }, []);

  useVisibilityPolling(fetchData, ADMIN_FAST);

  return { pendingWithdrawals, count, loading };
};

/**
 * Hook for admin pending KYC (visibility-aware polling)
 */
export const useAdminPendingKYC = () => {
  const [pendingKYC, setPendingKYC] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/admin/users?limit=500');
      const users = res.data?.users || [];
      const pending = users.filter(u => u.kyc_status === 'pending');
      setPendingKYC(pending);
      setCount(pending.length);
      setLoading(false);
    } catch { setLoading(false); }
  }, []);

  useVisibilityPolling(fetchData, ADMIN_NORMAL);

  return { pendingKYC, count, loading };
};

/**
 * Hook for admin active trades (visibility-aware polling)
 */
export const useAdminActiveTrades = () => {
  const [activeTrades, setActiveTrades] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/admin/binary-trades?status=active');
      const active = res.data?.trades || [];
      setActiveTrades(active);
      setCount(active.length);
      setLoading(false);
    } catch { setLoading(false); }
  }, []);

  useVisibilityPolling(fetchData, ADMIN_FAST);

  return { activeTrades, count, loading };
};

/**
 * Hook for admin user stats (visibility-aware polling)
 */
export const useAdminUsersStats = () => {
  const [totalUsers, setTotalUsers] = useState(0);
  const [activeUsers, setActiveUsers] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/admin/users?limit=500');
      const users = res.data?.users || [];
      setTotalUsers(users.length);
      setActiveUsers(users.filter(u => u.account_status === 'active').length);
      setLoading(false);
    } catch { setLoading(false); }
  }, []);

  useVisibilityPolling(fetchData, ADMIN_NORMAL);

  return { totalUsers, activeUsers, loading };
};

/**
 * Hook for admin activity feed (visibility-aware polling)
 */
export const useAdminActivityFeed = (limit = 20) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [depositsRes, withdrawalsRes, tradesRes] = await Promise.all([
        api.get('/admin/deposits').catch(() => ({ data: { deposits: [] } })),
        api.get('/admin/withdrawals').catch(() => ({ data: { withdrawals: [] } })),
        api.get('/admin/binary-trades').catch(() => ({ data: { trades: [] } }))
      ]);

      const depositActs = (depositsRes.data?.deposits || []).map(d => ({
        type: 'deposit',
        action: d.status === 'approved' ? 'Deposit Approved' : d.status === 'rejected' ? 'Deposit Rejected' : 'Deposit Submitted',
        user: d.user_email, amount: `$${fmtNumber(d.amount, 2)}`,
        timestamp: d.updated_at || d.created_at, status: d.status
      }));
      const withdrawalActs = (withdrawalsRes.data?.withdrawals || []).map(w => ({
        type: 'withdrawal',
        action: w.status === 'approved' ? 'Withdrawal Approved' : w.status === 'rejected' ? 'Withdrawal Rejected' : 'Withdrawal Requested',
        user: w.user_email, amount: `$${fmtNumber(w.amount, 2)}`,
        timestamp: w.updated_at || w.created_at, status: w.status
      }));
      const tradeActs = (tradesRes.data?.trades || []).map(t => {
        // Support both field names: binary uses status='won'/'lost', legacy/arbitrage uses outcome='win'/'loss'
        const isWon = t.status === 'won' || t.outcome === 'win';
        const isLost = t.status === 'lost' || t.outcome === 'loss';
        const isTerminal = isWon || isLost || t.status === 'completed';
        return {
          type: 'trade',
          action: isTerminal ? (isWon ? 'Trade Won' : 'Trade Lost') : 'Trade Created',
          user: t.user_email || 'User', amount: `$${fmtNumber(t.amount, 2)}`,
          timestamp: t.updated_at || t.created_at, status: t.status, outcome: t.outcome
        };
      });

      const all = [...depositActs, ...withdrawalActs, ...tradeActs]
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .slice(0, limit);

      setActivities(all);
      setLoading(false);
    } catch { setLoading(false); }
  }, [limit]);

  useVisibilityPolling(fetchData, ADMIN_NORMAL);

  return { activities, loading };
};

/**
 * Hook for admin dashboard metrics (visibility-aware polling)
 */
export const useAdminDashboardMetrics = () => {
  const [metrics, setMetrics] = useState({
    totalUsers: 0, activeUsers: 0, pendingDeposits: 0,
    pendingWithdrawals: 0, pendingKYC: 0, activeTrades: 0,
    totalVolume: 0, totalBalance: 0
  });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, depositsRes, withdrawalsRes, tradesRes] = await Promise.all([
        api.get('/admin/users?limit=500').catch(() => ({ data: { users: [] } })),
        api.get('/admin/deposits').catch(() => ({ data: { deposits: [] } })),
        api.get('/admin/withdrawals').catch(() => ({ data: { withdrawals: [] } })),
        api.get('/admin/binary-trades').catch(() => ({ data: { trades: [] } }))
      ]);

      const users = usersRes.data?.users || [];
      const deposits = depositsRes.data?.deposits || [];
      const withdrawals = withdrawalsRes.data?.withdrawals || [];
      const trades = tradesRes.data?.trades || [];

      setMetrics({
        totalUsers: users.length,
        activeUsers: users.filter(u => u.account_status === 'active').length,
        pendingDeposits: deposits.filter(d => d.status === 'pending').length,
        pendingWithdrawals: withdrawals.filter(w => w.status === 'pending').length,
        pendingKYC: users.filter(u => u.kyc_status === 'pending').length,
        activeTrades: trades.filter(t => t.status === 'active').length,
        totalVolume: trades.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0),
        totalBalance: users.reduce((sum, u) => sum + (parseFloat(u.balance) || 0), 0)
      });
      setLoading(false);
    } catch { setLoading(false); }
  }, []);

  useVisibilityPolling(fetchData, ADMIN_NORMAL);

  return { metrics, loading };
};

/**
 * Hook for admin platform addresses (visibility-aware polling)
 */
export const useAdminPlatformAddresses = () => {
  const [addresses, setAddresses] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/admin/deposit-addresses');
      setAddresses(res.data?.addresses || {});
      setLoading(false);
    } catch { setLoading(false); }
  }, []);

  useVisibilityPolling(fetchData, ADMIN_SLOW);

  return { addresses, loading };
};

/**
 * Hook for admin users with custom addresses (visibility-aware polling)
 */
export const useAdminUsersWithCustomAddresses = () => {
  const [usersWithCustomAddresses, setUsersWithCustomAddresses] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/admin/user-deposit-addresses');
      const data = res.data?.users || [];
      setUsersWithCustomAddresses(data);
      setCount(data.length);
      setLoading(false);
    } catch { setLoading(false); }
  }, []);

  useVisibilityPolling(fetchData, ADMIN_SLOW);

  return { usersWithCustomAddresses, count, loading };
};
